import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import SiteMap, { SITE_LABELS } from "@/components/injection/SiteMap";
import {
  getProtocol,
  logInjection,
  getHistory,
  incrementVialDose,
} from "@/lib/injectionApi";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

// Three-tap quick log:
//   1. Pick site (interactive body map, with recent-use heatmap)
//   2. Confirm compound + dose (pre-filled from protocol, editable)
//   3. Notes + difficulty + Save
//
// Pre-fills compound + dose from the member's protocol so a typical
// log is two taps (site → save). Local mirror so it works even if BE
// isn't shipped yet.
const STEPS = ["site", "dose", "notes"];

export default function InjectionLog() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [protocol, setProtocol] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [site, setSite] = useState(null);
  const [compound, setCompound] = useState("");
  const [doseMg, setDoseMg] = useState("");
  const [difficulty, setDifficulty] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    (async () => {
      const [p, h] = await Promise.all([
        getProtocol().catch(() => null),
        getHistory({ limit: 30 }).catch(() => ({ entries: [] })),
      ]);
      setProtocol(p);
      setRecent(h?.entries || []);
      if (p?.compound) setCompound(p.compound);
      if (p?.dose != null) setDoseMg(String(p.dose));
      setLoading(false);
    })();
  }, []);

  // Build a heatmap weighted by recency — last 14 days only, so the
  // member sees what to rotate AWAY from, not what they did last month.
  const heatmap = useMemo(() => {
    const cutoff = Date.now() - 14 * 86400000;
    const m = {};
    recent.forEach((e) => {
      if (!e.site) return;
      const ts = new Date(e.loggedAt).getTime();
      if (ts < cutoff) return;
      m[e.site] = (m[e.site] || 0) + 1;
    });
    return m;
  }, [recent]);

  const canNext = () => {
    if (step === 0) return !!site;
    if (step === 1) return !!compound && !!doseMg;
    return true;
  };

  const save = async () => {
    if (!site) {
      toast("Pick an injection site first.", "error");
      setStep(0);
      return;
    }
    setSaving(true);
    try {
      await logInjection({
        compound: compound.trim(),
        doseMg: parseFloat(doseMg) || null,
        site,
        loggedAt: new Date().toISOString(),
        notes: notes.trim() || undefined,
        difficulty: difficulty ?? undefined,
      });
      // Best-effort vial decrement — failure here doesn't invalidate
      // the log itself, so we swallow + tell the user separately if
      // they're tracking a vial. Most members won't have a vial yet.
      try { await incrementVialDose(); } catch { /* vial may not be started */ }
      toast("Injection logged. Nice work.", "success");
      navigate("/app/injection/history");
    } catch (err) {
      // 404 here means the BE couldn't find a consultation/subscription
      // to attach the log to — same root cause as the no-protocol gate
      // above but reached via a stale tab. Send the member somewhere
      // useful instead of asking them to keep retrying.
      if (err?.status === 404) {
        toast("Your protocol isn't on file yet — message care team if you're stuck.", "info");
        navigate("/app/plan");
        return;
      }
      toast(err?.detail || err?.message || "Couldn't save right now — please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: C.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner size={28} />
      </div>
    );
  }

  // Per BE contract, /injection/log returns 404 when no consultation /
  // subscription is linked. Catch that upstream by gating the form on
  // a real protocol — members without one see a clear "not ready yet"
  // surface instead of being pushed through 3 steps and toast-failing
  // at the end.
  if (!protocol) {
    return (
      <div data-testid="injection-log-no-protocol" style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: `1px solid ${C.warmGray3}` }}>
          <Logo size={20} />
          <button
            type="button"
            onClick={() => navigate("/app")}
            data-testid="log-noprotocol-back"
            style={{ background: "transparent", border: 0, color: C.warmGray, fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", cursor: "pointer" }}
          >
            ← DASHBOARD
          </button>
        </header>
        <main style={{ maxWidth: 640, margin: "0 auto", padding: "64px 24px 80px" }}>
          <Eyebrow color={C.peachDeep}>★ NO PROTOCOL ON FILE YET</Eyebrow>
          <Headline before="ALMOST" italic="there." size={52} className="mt-6" />
          <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.65, color: C.ink }}>
            Your physician hasn't finalized the protocol for an active subscription yet. The injection logger lights up the moment a protocol is on file.
          </p>
          <p style={{ marginTop: 14, fontSize: 14, lineHeight: 1.65, color: C.warmGray }}>
            If you're approved and waiting on your first shipment, hang tight — we'll email you when everything's ready.
          </p>
          <div style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Button variant="primary" size="md" testId="log-noprotocol-plan" onClick={() => navigate("/app/plan")}>
              VIEW MY PLAN →
            </Button>
            <Button variant="ghost" size="md" testId="log-noprotocol-careteam" onClick={() => navigate("/app/plan/messages")}>
              MESSAGE CARE TEAM
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-testid="injection-log" style={{ minHeight: "100vh", background: C.cream, color: C.ink, display: "flex", flexDirection: "column" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: `1px solid ${C.warmGray3}` }}>
        <Logo size={20} />
        <button
          type="button"
          onClick={() => navigate("/app")}
          data-testid="log-cancel"
          style={{ background: "transparent", border: 0, color: C.warmGray, fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", cursor: "pointer" }}
        >
          CANCEL ✕
        </button>
      </header>

      {/* Progress strip */}
      <div data-testid="log-progress" style={{ display: "flex", gap: 6, padding: "14px 24px", background: C.bone, borderBottom: `1px solid ${C.warmGray3}` }}>
        {STEPS.map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 4,
              background: i <= step ? C.peachDeep : "rgba(122,107,93,0.18)",
              transition: "background 200ms",
            }}
          />
        ))}
      </div>

      <main style={{ flex: 1, padding: "32px 24px 80px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          {step === 0 && (
            <div>
              <Eyebrow color={C.peachDeep}>★ STEP 01 — WHERE</Eyebrow>
              <Headline before="PICK YOUR" italic="site." size={48} className="mt-4" />
              <p style={{ marginTop: 14, fontSize: 14, color: C.warmGray, lineHeight: 1.65, maxWidth: 540 }}>
                Rotate at least an inch from your last spot. Sites you've used in the last 2 weeks are tinted — pick a rested one.
              </p>
              <div style={{ marginTop: 28 }}>
                <SiteMap selected={site} onSelect={setSite} heatmap={heatmap} testId="log-site-map" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <Eyebrow color={C.peachDeep}>★ STEP 02 — WHAT</Eyebrow>
              <Headline before="CONFIRM YOUR" italic="dose." size={48} className="mt-4" />
              <p style={{ marginTop: 14, fontSize: 14, color: C.warmGray, lineHeight: 1.65, maxWidth: 540 }}>
                We've pre-filled from your protocol. Edit only if your physician changed it.
              </p>
              <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 18 }}>
                <LabeledInput
                  label="COMPOUND"
                  value={compound}
                  onChange={setCompound}
                  testId="log-compound-input"
                  placeholder="e.g. Semaglutide"
                />
                <LabeledInput
                  label="DOSE (MG)"
                  value={doseMg}
                  onChange={setDoseMg}
                  testId="log-dose-input"
                  placeholder="0.25"
                  inputMode="decimal"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <Eyebrow color={C.peachDeep}>★ STEP 03 — HOW IT FELT</Eyebrow>
              <Headline before="ANY" italic="notes?" size={48} className="mt-4" />
              <p style={{ marginTop: 14, fontSize: 14, color: C.warmGray, lineHeight: 1.65, maxWidth: 540 }}>
                Optional — but small notes add up. Side effects, anything weird, how the technique felt.
              </p>

              <div style={{ marginTop: 28 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 10 }}>
                  HOW DIFFICULT WAS THIS INJECTION?
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`log-difficulty-${n}`}
                      onClick={() => setDifficulty(n === difficulty ? null : n)}
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: "50%",
                        border: `1.5px solid ${C.ink}`,
                        background: difficulty === n ? C.ink : C.pure,
                        color: difficulty === n ? C.cream : C.ink,
                        fontSize: 16,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 11, color: C.warmGray, letterSpacing: "0.1em" }}>
                  <span>EASY</span><span>HARD</span>
                </div>
              </div>

              <div style={{ marginTop: 28 }}>
                <label
                  htmlFor="log-notes-textarea"
                  style={{ display: "block", fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 8 }}
                >
                  NOTES (OPTIONAL)
                </label>
                <textarea
                  id="log-notes-textarea"
                  data-testid="log-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="e.g. mild redness, took longer to dissolve…"
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    fontSize: 14,
                    background: C.pure,
                    border: `1px solid ${C.warmGray3}`,
                    outline: "none",
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              <div style={{ marginTop: 22, padding: 18, background: C.bone, fontSize: 14, lineHeight: 1.55 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep }}>★ SUMMARY</div>
                <div style={{ marginTop: 8 }}>
                  <strong>{compound || "—"}</strong> · {doseMg || "—"}mg
                </div>
                <div style={{ color: C.warmGray }}>
                  {SITE_LABELS[site] || site}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer
        style={{
          borderTop: `1px solid ${C.warmGray3}`,
          padding: "16px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: C.cream,
          position: "sticky",
          bottom: 0,
        }}
      >
        {step > 0 ? (
          <button
            type="button"
            onClick={() => setStep(step - 1)}
            data-testid="log-back"
            style={{ background: "transparent", border: 0, color: C.warmGray, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", cursor: "pointer" }}
          >
            ← BACK
          </button>
        ) : <span />}
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray }}>
          STEP {String(step + 1).padStart(2, "0")} / 0{STEPS.length}
        </span>
        {step < STEPS.length - 1 ? (
          <Button
            variant="primary"
            size="lg"
            testId="log-next"
            disabled={!canNext()}
            onClick={() => setStep(step + 1)}
          >
            NEXT →
          </Button>
        ) : (
          <Button
            variant="primary"
            size="lg"
            testId="log-save"
            disabled={saving}
            onClick={save}
          >
            {saving ? "SAVING…" : "SAVE LOG →"}
          </Button>
        )}
      </footer>
    </div>
  );
}

function LabeledInput({ label, value, onChange, testId, placeholder, inputMode }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 6 }}>
        {label}
      </span>
      <input
        type="text"
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        placeholder={placeholder}
        style={{
          width: "100%",
          padding: "14px 16px",
          fontSize: 16,
          background: C.pure,
          border: `1px solid ${C.warmGray3}`,
          outline: "none",
          fontFamily: "inherit",
        }}
      />
    </label>
  );
}
